export type StorageResult<T> = Readonly<{ ok: true; value: T } | { ok: false; error: unknown }>;

export interface StorageAdapter {
  getItem(key: string): StorageResult<string | null>;
  setItem(key: string, value: string): StorageResult<void>;
  removeItem(key: string): StorageResult<void>;
}

export class MemoryStorageAdapter implements StorageAdapter {
  private readonly values = new Map<string, string>();
  public getItem(key: string): StorageResult<string | null> { return { ok: true, value: this.values.get(key) ?? null }; }
  public setItem(key: string, value: string): StorageResult<void> { this.values.set(key, value); return { ok: true, value: undefined }; }
  public removeItem(key: string): StorageResult<void> { this.values.delete(key); return { ok: true, value: undefined }; }
}

export class BrowserStorageAdapter implements StorageAdapter {
  private readonly storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  private readonly initializationError: unknown;
  public constructor(storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>) {
    if (storage !== undefined) { this.storage = storage; this.initializationError = null; return; }
    try { this.storage = globalThis.localStorage; this.initializationError = null; }
    catch (error) { this.storage = null; this.initializationError = error; }
  }
  public getItem(key: string): StorageResult<string | null> { return this.attempt(() => this.requireStorage().getItem(key)); }
  public setItem(key: string, value: string): StorageResult<void> { return this.attempt(() => this.requireStorage().setItem(key, value)); }
  public removeItem(key: string): StorageResult<void> { return this.attempt(() => this.requireStorage().removeItem(key)); }
  private attempt<T>(operation: () => T): StorageResult<T> {
    try { return { ok: true, value: operation() }; } catch (error) { return { ok: false, error }; }
  }
  private requireStorage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> {
    if (this.storage === null) {
      throw this.initializationError instanceof Error ? this.initializationError : new Error('Storage unavailable.');
    }
    return this.storage;
  }
}
